import c from 'classnames';
import React from 'react';

import { Typography } from '@repo/ui';
import { userValueFormat } from 'helpers';
import { RangesGraph } from 'views/Results/Insights/RangesGraph/RangesGraph';
import { EvaluationPillFromColor } from 'views/Results/Insights/SubMetricGraphs/SubMetricGraphs';
import { MainFinding } from 'views/Results/Summary/SummaryCards/NoteCard/useMainFindings';

import styles from './RetestPriorityCard.module.css';

// Taxa names are italicised by convention; metabolites are not.
const ITALIC_TAXA_LEVELS = ['species', 'genus'];

interface RetestPriorityCardProps {
  metric: MainFinding;
}

export const RetestPriorityCard: React.FC<RetestPriorityCardProps> = ({
  metric,
}) => {
  const dataType = metric.gvh?.data_type || 'number';
  const ranges = metric.gvh?.gut_vaginal_health_version_ranges;

  const isItalic = ITALIC_TAXA_LEVELS.includes(metric.gvh?.taxa_level ?? '');

  const valueDisplay = userValueFormat(
    metric.user_value,
    dataType,
    metric.user_string,
  );

  // Same guard as the metric detail page and the PDF report.
  const canRenderBar =
    dataType !== 'string' && !!metric.gvh?.range_bar_display && !!ranges?.length;

  return (
    <div className={styles.card}>
      <div className={styles.left}>
        <EvaluationPillFromColor
          evaluation_color={metric.evaluation_color}
          showIconOnly
        />
        <Typography variant='body-s' italic={isItalic}>
          {metric.gvh?.name}
        </Typography>
      </div>

      <div className={c(styles.right, !canRenderBar && styles.valueOnly)}>
        {canRenderBar ? (
          <RangesGraph
            dataType={dataType}
            relativeAbundance={metric.user_value}
            ranges={ranges}
            scaleBarMin={metric.gvh?.scale_bar_min}
            scaleBarMax={metric.gvh?.scale_bar_max}
            evaluationColor={metric.evaluation_color}
            valueLabel={valueDisplay}
            withDetails={false}
            withSlimBars
          />
        ) : (
          <Typography variant='body-s'>{valueDisplay}</Typography>
        )}
      </div>
    </div>
  );
};

export default RetestPriorityCard;
